import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, CheckCircle2, Clock, XCircle, FileText, CalendarClock, Check, HardDrive, Infinity as InfinityIcon, Send } from "lucide-react";
import { api, fmtPay } from "../lib/api";
import { useLang, panelRentalEur } from "../context/LanguageContext";
import { PanelRentalCard } from "./Landing";
import { GlassCard, PageHeader, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";
import { planStyle, fmtStorage } from "../lib/planStyles";

const STATUS_STYLE = {
  approved: { icon: CheckCircle2, cls: "text-emerald-300", label: "Aprovado" },
  pending: { icon: Clock, cls: "text-amber-300", label: "Pendente" },
  created: { icon: Clock, cls: "text-slate-400", label: "Criado" },
  rejected: { icon: XCircle, cls: "text-rose-400", label: "Rejeitado" },
  cancelled: { icon: XCircle, cls: "text-rose-400", label: "Cancelado" },
};

export default function Billing() {
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [myPlan, setMyPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [buying, setBuying] = useState(null);
  const { t, fmtPrice } = useLang();
  const [tb, setTb] = useState(11);
  const [customPrice, setCustomPrice] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/saas/plans/suggest?storage_gb=${tb * 1024}`).then((r) => setCustomPrice(r.data.price_eur)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [tb]);

  const TG_URL = "https://t.me/streamingtv2020";
  const openTelegram = (text) => {
    window.open(`${TG_URL}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const buyCustom = async () => {
    openTelegram(`Olá! Quero o plano Sob Medida de ${tb} TB${customPrice ? ` (${fmtPrice(customPrice + panelRentalEur(tb * 1024))}/mês)` : ""} no CineBot.`);
    toast.info("Abrindo o Telegram para combinar o seu plano...");
  };

  const loadMyPlan = () => api.get("/saas/my-plan").then((r) => setMyPlan(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/saas/plans").then((r) => setPlans(r.data)).catch(() => {});
    loadMyPlan();
    api.get("/payments/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/invoices/mine").then((r) => setInvoices(r.data)).catch(() => {});
    const status = params.get("status");
    if (status === "success") toast.success("Pagamento aprovado! Plano ativado/renovado por +30 dias.");
    if (status === "pending") toast.info("Pagamento pendente — o plano ativa assim que confirmar.");
    if (status === "failure") toast.error("Pagamento não concluído. Tente novamente.");
    if (status) {
      const sid = params.get("session_id");
      if (status === "success" && sid) api.get(`/payments/status/${sid}`).catch(() => {});
      setParams({}, { replace: true });
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      setTimeout(() => {
        loadMyPlan();
        api.get("/payments/history").then((r) => setHistory(r.data)).catch(() => {});
      }, 1500);
    }
  }, []);

  const buy = async (plan) => {
    openTelegram(`Olá! Quero migrar para o plano ${plan.name} (${fmtStorage(plan.storage_gb)}) a ${fmtPrice((plan.price_eur ?? plan.price_brl ?? 0) + panelRentalEur(plan.storage_gb))}/mês no CineBot.`);
    toast.info("Abrindo o Telegram para ativar o seu plano...");
  };

  const payInvoice = async (inv) => {
    openTelegram(`Olá! Quero pagar a fatura "${inv.title}" (${fmtPay(inv)}) do CineBot.`);
    toast.info("Abrindo o Telegram para pagar a fatura...");
  };

  const quotaGb = myPlan?.plan?.storage_gb || 0;
  const usedGb = myPlan?.storage_used_gb || 0;
  const usedPct = quotaGb ? Math.min(100, (usedGb / quotaGb) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader eyebrow="Assinatura" title="Planos & Armazenamento" subtitle="Mensalidade fixa por armazenamento NVMe. Streaming ilimitado em todos os planos — sem desconto por GB." />

      <TutorialHint
        id="billing"
        title="Como funcionam os planos"
        steps={[
          "Escolha o plano pelo espaço de armazenamento de que precisa (250 GB a 10 TB).",
          "Para ativar, clique no plano e fale comigo no Telegram — ativação rápida após o pagamento.",
          "Cada pagamento soma +30 dias de validade (renovar antes do vencimento acumula).",
          "O streaming dos seus links é ilimitado: nada é descontado por GB assistido.",
        ]}
      />

      <GlassCard className="p-6 anim-fade-up" data-testid="current-plan-card">
        <div className="flex flex-wrap items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
            {myPlan?.unlimited ? <Crown size={26} className="text-amber-400" /> : <HardDrive size={26} className="text-cyan-400" />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500">{t("your_sub")}</p>
            {myPlan?.unlimited ? (
              <p className="font-display text-2xl font-extrabold text-amber-300 mt-1" data-testid="current-plan-name">{t("unlimited_admin")}</p>
            ) : myPlan?.active ? (
              <>
                <p className="font-display text-2xl font-extrabold neon-text mt-1" data-testid="current-plan-name">
                  {myPlan.plan?.name} · {fmtStorage(quotaGb)}
                </p>
                <p className="text-[11px] text-slate-500 mt-1" data-testid="current-plan-expiry">
                  {t("valid_until")} {new Date(myPlan.plan_expires_at).toLocaleDateString("pt-PT")} · <span className="text-emerald-300 inline-flex items-center gap-1"><InfinityIcon size={11} /> {t("unlimited_stream")}</span>
                </p>
              </>
            ) : (
              <p className="font-display text-xl font-bold text-rose-300 mt-1" data-testid="current-plan-name">{t("no_plan")}</p>
            )}
          </div>
          {myPlan?.active && !myPlan?.unlimited && quotaGb > 0 && (
            <div className="w-full sm:w-64">
              <div className="flex justify-between text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1.5">
                <span>{t("storage_used")}</span>
                <span>{usedGb.toLocaleString("pt-PT")} / {quotaGb.toLocaleString("pt-PT")} GB</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div
                  data-testid="billing-storage-progress"
                  className={`h-full rounded-full transition-all duration-700 ${usedPct > 90 ? "bg-rose-500" : usedPct > 70 ? "bg-amber-400" : "bg-gradient-to-r from-cyan-500 to-emerald-400"}`}
                  style={{ width: `${Math.max(2, usedPct)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </GlassCard>

      <PanelRentalCard dataTestid="panel-rental-billing" />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.map((p) => {
          const s = planStyle(p.color);
          const key = p.id.replace("plan_", "");
          const isCurrent = myPlan?.plan?.id === p.id && myPlan?.active;
          const storageEur = p.price_eur ?? p.price_brl ?? 0;
          const rent = panelRentalEur(p.storage_gb);
          const total = storageEur + rent;
          return (
            <GlassCard key={p.id} data-testid={`plan-card-${key}`} className={`p-7 flex flex-col border ${s.ring} ${s.glow} transition-all duration-300 anim-fade-up`}>
              <div className="flex items-center justify-between">
                <p className="font-display text-white font-semibold">{p.name}</p>
                {p.badge && <span className={`text-[9px] font-mono2 uppercase tracking-widest border rounded-full px-2.5 py-1 ${s.chip}`}>{p.badge}</span>}
              </div>
              <p className={`font-display text-3xl font-extrabold mt-3 ${s.text}`} data-testid={`plan-total-billing-${key}`}>{fmtPrice(total)}<span className="text-sm text-slate-500 font-normal">{t("per_month")}</span></p>
              <p className="text-[10px] text-slate-500 mt-1 font-mono2" data-testid={`plan-breakdown-billing-${key}`}>{fmtPrice(storageEur)} {t("storage_word")} + {fmtPrice(rent)} {t("panel_word")}</p>
              <p className="text-xs text-slate-400 mt-1 font-mono2">{fmtStorage(p.storage_gb)} {t("nvme_storage")}</p>
              <ul className="mt-4 space-y-2 text-xs text-slate-400 flex-1">
                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_stream")}</li>
                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_renew")}</li>
              </ul>
              <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-500">
                <Send size={12} className="text-sky-400" /> Telegram <span className="text-sky-300 font-semibold">@streamingtv2020</span>
              </div>
              <button
                data-testid={`plan-subscribe-btn-${key}`}
                onClick={() => buy(p)}
                disabled={buying === p.id || isCurrent}
                className={`mt-5 w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors ${isCurrent ? "bg-slate-800 text-slate-500 cursor-default" : s.btn}`}
              >
                {buying === p.id ? <Spinner /> : isCurrent ? t("current_plan_btn") : myPlan?.trial ? `${t("subscribe")} ${p.name}` : myPlan?.active ? `${t("migrate_to")} ${p.name}` : `${t("subscribe")} ${p.name}`}
              </button>
            </GlassCard>
          );
        })}
      </div>

      <GlassCard className="p-7 border border-cyan-400/25 anim-fade-up" data-testid="custom-plan-card-billing">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display text-white font-semibold">{t("custom_title")}</p>
              <span className="text-[9px] font-mono2 uppercase tracking-widest text-cyan-300 border border-cyan-400/30 bg-cyan-400/10 rounded-full px-2.5 py-1">{t("custom_badge")}</span>
            </div>
            <p className="text-xs text-slate-400 mb-4">{t("custom_desc_billing")}</p>
            <input data-testid="custom-tb-slider-billing" type="range" min="11" max="200" step="1" value={tb} onChange={(e) => setTb(Number(e.target.value))} className="w-full accent-cyan-400" />
            <div className="flex justify-between text-[10px] font-mono2 text-slate-500 mt-1"><span>11 TB</span><span>200 TB</span></div>
          </div>
          <div className="text-center lg:text-right shrink-0">
            <p className="font-display text-3xl font-extrabold text-cyan-300" data-testid="custom-tb-value-billing">{tb} TB</p>
            <p className="font-display text-xl font-bold text-white mt-1" data-testid="custom-price-billing">{customPrice ? `${fmtPrice(customPrice + panelRentalEur(tb * 1024))}${t("per_month")}` : "…"}</p>
            {customPrice && (
              <p className="text-[10px] text-slate-500 font-mono2 mt-0.5" data-testid="custom-breakdown-billing">{fmtPrice(customPrice)} {t("storage_word")} + {fmtPrice(panelRentalEur(tb * 1024))} {t("panel_word")}</p>
            )}
            <button data-testid="custom-plan-buy-btn" onClick={buyCustom} disabled={buying === "custom" || !customPrice} className="btn-neon px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2 mt-4">
              {buying === "custom" ? <Spinner /> : myPlan?.active ? t("migrate_custom") : t("subscribe_custom")}
            </button>
          </div>
        </div>
      </GlassCard>

      {invoices.length > 0 && (
        <GlassCard className="overflow-hidden anim-fade-up" data-testid="my-invoices-card">
          <div className="px-6 pt-6 pb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-400/10 border border-purple-400/25 flex items-center justify-center">
              <FileText size={18} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">{t("my_invoices")}</h3>
              <p className="text-[11px] text-slate-500">{t("invoices_sub")}</p>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            {invoices.map((inv) => (
              <div key={inv.id} data-testid={`my-invoice-${inv.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800/60 bg-slate-950/40 px-4 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-semibold truncate">{inv.title}</p>
                  <p className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 mt-0.5">
                    <CalendarClock size={11} /> {t("due_on")} {new Date(inv.due_date.length === 10 ? `${inv.due_date}T12:00:00` : inv.due_date).toLocaleDateString("pt-PT")}
                  </p>
                </div>
                <p className="text-sm font-bold text-white font-mono2">{fmtPay(inv)}</p>
                {inv.status === "pending" ? (
                  <button data-testid={`pay-invoice-${inv.id}`} onClick={() => payInvoice(inv)} disabled={buying === inv.id} className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
                    {buying === inv.id ? <Spinner /> : t("pay_now")}
                  </button>
                ) : (
                  <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${inv.status === "paid" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-slate-500 border-slate-700 bg-slate-800/40"}`} data-testid={`my-invoice-status-${inv.id}`}>
                    {inv.status === "paid" ? t("paid") : t("cancelled")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <GlassCard className="overflow-hidden anim-fade-up anim-delay-2" data-testid="payment-history-card">
        <div className="px-6 pt-6 pb-4">
          <h3 className="font-display text-white font-semibold text-sm">{t("history_title")}</h3>
        </div>
        {history.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-slate-500" data-testid="history-empty">{t("history_empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-6 py-3">Item</th>
                  <th className="px-6 py-3">Valor</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Data</th>
                </tr>
              </thead>
              <tbody data-testid="payment-history-body">
                {history.map((h) => {
                  const s = STATUS_STYLE[h.status] || STATUS_STYLE.created;
                  return (
                    <tr key={h.id} className="border-b border-slate-800/40 last:border-0" data-testid={`payment-row-${h.id}`}>
                      <td className="px-6 py-3.5 text-white">{h.kind === "plan" ? `Plano ${h.plan_name} (mensal)` : `${h.credits_gb} GB (créditos)`}</td>
                      <td className="px-6 py-3.5 text-slate-300">{fmtPay(h)}</td>
                      <td className="px-6 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-xs ${s.cls}`}>
                          <s.icon size={13} /> {s.label}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-xs text-slate-500">{new Date(h.created_at * 1000).toLocaleString("pt-PT")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
